// src/components/Users/Profile.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { fetchUserProfile } from '../../redux/slices/Users/userProfileSlice';
import { fetchDogById } from '../../redux/slices/Dogs/dogsSlice';
import DogDetailsModal from '../Dogs/DogDetailsModal';
import AddDogModal from '../Dogs/AddDogModal';
import AddressSection from './AddressSection';

const Profile = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { profile, isLoading, error } = useSelector((state) => state.userProfile);
  const { user: authUser } = useSelector((state) => state.auth);
  const [selectedDogId, setSelectedDogId] = useState(null);
  const [selectedDogData, setSelectedDogData] = useState(null);
  const [loadingDog, setLoadingDog] = useState(false);
  const [showAddDogModal, setShowAddDogModal] = useState(false);

  useEffect(() => {
    if (authUser) {
      dispatch(fetchUserProfile());
    }
  }, [dispatch, authUser]);

  const handleDogAdded = () => {
    dispatch(fetchUserProfile());
  };

  const handleDogClick = async (dog) => {
    setSelectedDogId(dog._id);
    setLoadingDog(true);
    try {
      const freshDog = await dispatch(fetchDogById(dog._id)).unwrap();
      setSelectedDogData(freshDog);
    } catch (err) {
      console.error('Failed to fetch dog details', err);
      setSelectedDogData(dog);
    } finally {
      setLoadingDog(false);
    }
  };

  const handleDogUpdate = () => {
    dispatch(fetchUserProfile());
    if (selectedDogId) {
      dispatch(fetchDogById(selectedDogId)).then((res) => {
        if (res.payload) setSelectedDogData(res.payload);
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64 bg-[#EFE6DD]">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#9A0002] border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-[#9A0002]/10 border border-[#9A0002]/30 text-[#9A0002] px-4 py-3 rounded-xl">
          {t('common.errorLoading')}: {error}
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const hasDogs = profile.dogs && profile.dogs.length > 0;
  const canBook = profile.hasShippingAddress && hasDogs;

  return (
    <>
      <Helmet>
        <title>{t('meta.profile.title')}</title>
        <meta name="description" content={t('meta.profile.description')} />
        <link rel="canonical" href="https://www.hotelmacarios.com.mx/profile" />
      </Helmet>

      <div className="bg-[#EFE6DD] min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          {/* User Info Card */}
          <div className="bg-white rounded-2xl border border-[#3A2A20]/10 overflow-hidden mb-8">
            <div className="bg-[#9A0002] px-6 py-5">
              <h1 className="font-['Fraunces'] text-2xl font-semibold text-[#EFE6DD]">
                {t('profile.title')}
              </h1>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/50">
                    {t('profile.fullName')}
                  </p>
                  <p className="text-base font-medium text-[#3A2A20] mt-1">{profile.fullname}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/50">
                    {t('profile.email')}
                  </p>
                  <p className="text-base font-medium text-[#3A2A20] mt-1">{profile.email}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#3A2A20]/50">
                    {t('profile.memberSince')}
                  </p>
                  <p className="text-base font-medium text-[#3A2A20] mt-1">
                    {new Date(profile.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Address Section */}
          <AddressSection profile={profile} />

          {/* Dogs Section */}
          <div className="bg-white rounded-2xl border border-[#3A2A20]/10 overflow-hidden mb-8">
            <div className="px-6 py-4 border-b border-[#3A2A20]/10 flex justify-between items-center">
              <h2 className="font-['Fraunces'] text-xl font-semibold text-[#3A2A20]">
                {t('profile.myDogs')} ({profile.dogs?.length || 0})
              </h2>
              <button
                onClick={() => setShowAddDogModal(true)}
                className="bg-[#9A0002] hover:bg-[#7A0001] text-[#EFE6DD] px-4 py-2 rounded-xl text-sm font-medium transition"
              >
                {t('profile.addDog')}
              </button>
            </div>
            <div className="p-6">
              {hasDogs ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {profile.dogs.map((dog) => (
                    <div
                      key={dog._id}
                      className="bg-[#EFE6DD]/40 border border-[#3A2A20]/10 rounded-xl overflow-hidden hover:shadow-md hover:border-[#9A0002]/30 transition cursor-pointer"
                      onClick={() => handleDogClick(dog)}
                    >
                      <div className="h-48 overflow-hidden bg-[#3A2A20]/10">
                        {dog.image ? (
                          <img src={dog.image} alt={dog.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#3A2A20]/40 text-sm">
                            {t('profile.noImage')}
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-['Fraunces'] text-lg font-semibold text-[#3A2A20]">{dog.name}</h3>
                        <p className="text-sm text-[#3A2A20]/60 mt-1">{t('profile.breed')}: {dog.breed}</p>
                        <p className="text-sm text-[#3A2A20]/60">{t('profile.age')}: {dog.age} {t('profile.years')}</p>
                        <p className="text-sm text-[#3A2A20]/60">{t('profile.weight')}: {dog.weight} kg</p>
                        <p className="text-sm text-[#3A2A20]/60">
                          {t('profile.gender')}: {dog.gender === 'M' ? t('profile.male') : t('profile.female')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#3A2A20]/50 text-center py-8">
                  {t('profile.noDogs')}
                </p>
              )}
            </div>
          </div>

          {/* Gate hacia Rooms */}
          <div className="bg-white rounded-2xl border border-[#3A2A20]/10 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-[#3A2A20]">
                {canBook ? t('profile.readyToBook') : t('profile.completeToBook')}
              </p>
              {!canBook && (
                <ul className="text-xs text-[#3A2A20]/50 mt-1 space-y-0.5">
                  {!profile.hasShippingAddress && <li>• {t('profile.missingAddress')}</li>}
                  {!hasDogs && <li>• {t('profile.missingDog')}</li>}
                </ul>
              )}
            </div>
            {canBook ? (
              <Link
                to="/rooms"
                className="px-6 py-3 bg-[#9A0002] hover:bg-[#7A0001] text-[#EFE6DD] rounded-xl text-sm font-medium transition whitespace-nowrap"
              >
                {t('profile.continueToRooms')}
              </Link>
            ) : (
              <button
                disabled
                className="px-6 py-3 bg-[#3A2A20]/10 text-[#3A2A20]/40 rounded-xl text-sm font-medium cursor-not-allowed whitespace-nowrap"
              >
                {t('profile.continueToRooms')}
              </button>
            )}
          </div>
        </div>

        <DogDetailsModal
          isOpen={!!selectedDogId}
          onClose={() => {
            setSelectedDogId(null);
            setSelectedDogData(null);
          }}
          dog={selectedDogData}
          onDogUpdate={handleDogUpdate}
        />

        <AddDogModal
          isOpen={showAddDogModal}
          onClose={() => setShowAddDogModal(false)}
          onDogAdded={handleDogAdded}
        />
      </div>
    </>
  );
};

export default Profile;